import React from 'react';
import type Traveler from '@this/domain/traveler/traveler';
import type TravelerList from '@this/domain/traveler/traveler_list';
import type User from '@this/domain/user/user';
import SelectTravelers from '../../../reserve_trip/reserve_confirm/select_travelers/select_travelers';

interface TravelerFormProps {
  traveler: Traveler;
  index: number;
  user: User;
  travelers: TravelerList;
  members: User[];
  isFlightCustomerInfoRequired: boolean;
  isHotelInfoRequired: boolean;
  onTravelerTypeChange: (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTravelerSelect: (i: number, traveler: Traveler) => void;
}

const TravelerForm: React.FC<TravelerFormProps> = ({
  traveler,
  index,
  user,
  travelers,
  members,
  isFlightCustomerInfoRequired,
  isHotelInfoRequired,
  onTravelerTypeChange,
  onTravelerSelect
}) => {
  return (
    <div>
      <p
        className="reserve-confirm__traveler__peoplenum"
        data-testid={`arrangement-request-traveler-label-${index}`}
      >
        {index + 1}人目
      </p>
      <div className="reserve-confirm__traveler">
        {/* - user=reserveInfo.user */}
        <label className="reserve-confirm__traveler__type" htmlFor={`traveler-${index}-self`}>
          <input
            id={`traveler-${index}-self`}
            data-testid={`arrangement-request-traveler-type-self-${index}`}
            type="radio"
            value="self"
            onChange={onTravelerTypeChange(index)}
            checked={traveler.type === 'self'}
          />
          <span>自分の手配をする</span>
        </label>
        {traveler.type === 'self' && (
          <div className="reserve-confirm__traveler__from-member">
            <div className="select-travelers__selected-list__item">
              <div className="members-list__item__info">
                {user.departmentName() && <p className="members-list__item__name">{user.departmentName()}</p>}
                <p className="members-list__item__name">{user.name}</p>
                <p className="members-list__item__birthday">({user.email})</p>
              </div>
            </div>
          </div>
        )}
        {user.show_other_members && members && members.length > 0 && (
          <>
            <label className="reserve-confirm__traveler__type" htmlFor={`traveler-${index}-member`}>
              <input
                id={`traveler-${index}-member`}
                data-testid={`arrangement-request-traveler-type-member-${index}`}
                type="radio"
                value="member"
                onChange={onTravelerTypeChange(index)}
                checked={traveler.type === 'member'}
              />
              <span>他の人の手配をする</span>
            </label>
            {traveler.type === 'member' && (
              <div className="reserve-confirm__traveler__from-member">
                <SelectTravelers travelers={travelers.list} currentIndex={index} onSelect={onTravelerSelect} />
              </div>
            )}
          </>
        )}
        <label className="reserve-confirm__traveler__type" htmlFor={`traveler-${index}-companion`}>
          <input
            id={`traveler-${index}-companion`}
            data-testid={`arrangement-request-traveler-type-companion-${index}`}
            className="companion-label"
            type="radio"
            value="companion"
            onChange={onTravelerTypeChange(index)}
            checked={traveler.type === 'companion'}
          />
          <span>手動で入力</span>
        </label>
        {traveler.type === 'companion' && (
          <div className="reserve-confirm__traveler__from-member">
            <div className="reserve-confirm__traveler__input-area">
              <div className="reserve-confirm__label">氏名（ローマ字）</div>
              <div className="reserve-confirm__traveler__input-area-right">
                <input
                  data-testid={`arrangement-request-traveler-last-name-roman-${index}`}
                  className="reserve-confirm__input-name last-name"
                  type="text"
                  placeholder="例) Yamada"
                  value={traveler.lastNameRoman}
                  onChange={travelers.handleTravelerInfoChange(index, 'setLastNameRoman')}
                />
                <input
                  data-testid={`arrangement-request-traveler-first-name-roman-${index}`}
                  className="reserve-confirm__input-name first-name"
                  type="text"
                  placeholder="例) Ichiro"
                  value={traveler.firstNameRoman}
                  onChange={travelers.handleTravelerInfoChange(index, 'setFirstNameRoman')}
                />
              </div>
            </div>
            <div className="reserve-confirm__traveler__input-area">
              <div className="reserve-confirm__label">氏名（カナ）</div>
              <div className="reserve-confirm__traveler__input-area-right">
                <input
                  data-testid={`arrangement-request-traveler-last-name-kana-${index}`}
                  className="reserve-confirm__input-name last-name"
                  type="text"
                  placeholder="例) ヤマダ"
                  value={traveler.lastNameKana}
                  onChange={travelers.handleTravelerInfoChange(index, 'setLastNameKana')}
                />
                <input
                  data-testid={`arrangement-request-traveler-first-name-kana-${index}`}
                  className="reserve-confirm__input-name first-name"
                  type="text"
                  placeholder="例) イチロウ"
                  value={traveler.firstNameKana}
                  onChange={travelers.handleTravelerInfoChange(index, 'setFirstNameKana')}
                />
              </div>
            </div>
            {isFlightCustomerInfoRequired && (
              <div className="reserve-confirm__traveler__input-area">
                <div className="reserve-confirm__label">誕生日</div>
                <div className="reserve-confirm__traveler__input-area-right">
                  <input
                    className="reserve-confirm__input-birthday"
                    type="text"
                    placeholder="例) 1986-01-01"
                    value={traveler.birthday}
                    onChange={travelers.handleTravelerInfoChange(index, 'setBirthday')}
                  />
                </div>
              </div>
            )}
            {(isFlightCustomerInfoRequired || isHotelInfoRequired) && (
              <div className="reserve-confirm__traveler__input-area">
                <div className="reserve-confirm__label">性別</div>
                <div className="reserve-confirm__traveler__input-area-right">
                  <label className="reserve-confirm__radio-label" htmlFor={`traveler-${index}-male`}>
                    <input
                      id={`traveler-${index}-male`}
                      className="reserve-confirm__radio-input"
                      type="radio"
                      value="male"
                      checked={traveler.flightGender === 'male'}
                      onChange={travelers.handleTravelerInfoChange(index, 'setFlightGender')}
                    />
                    <span>男性</span>
                  </label>
                  <label className="reserve-confirm__radio-label" htmlFor={`traveler-${index}-female`}>
                    <input
                      id={`traveler-${index}-female`}
                      className="reserve-confirm__radio-input"
                      type="radio"
                      value="female"
                      checked={traveler.flightGender === 'female'}
                      onChange={travelers.handleTravelerInfoChange(index, 'setFlightGender')}
                    />
                    <span>女性</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelerForm;
